import { Component } from '@theme/component';

/**
 * @typedef {Object} SizeSelectorRefs
 * @property {HTMLInputElement[]} [sizeRadios] - The size buttons
 * @property {HTMLSelectElement} [customSelect] - The "Custom" dropdown
 * @property {HTMLInputElement} [customValue] - Hidden field carrying a dropdown choice
 * @property {HTMLElement} [errorMessage] - Shown when nothing is chosen
 */

/**
 * Size chosen as a line item property rather than a variant.
 *
 * Two jobs:
 *
 * 1. KEEP ONE VALUE. Only the radios are named `properties[<key>]`; the dropdown
 *    is unnamed and routes its choice through a hidden input that is enabled
 *    only while a custom size is picked. Choosing from one clears the other, so
 *    exactly one field is submitted.
 *
 * 2. REQUIRE A CHOICE. A variant is always preselected; a property is not, so
 *    without this an order could arrive with no size at all. Add to cart stays
 *    disabled until something is picked.
 *
 * @extends {Component<SizeSelectorRefs>}
 */
class SizeSelectorComponent extends Component {
  /** @type {AbortController} */
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#abortController;

    this.addEventListener('change', this.#handleChange, { signal });
    this.#syncCustomValue();
    this.#applyState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /** @param {Event} event */
  #handleChange = (event) => {
    const target = event.target;
    const select = this.#select;

    if (target instanceof HTMLSelectElement) {
      // A custom measurement wins: drop the button selection entirely.
      if (target.value) for (const radio of this.#radios) radio.checked = false;
    } else if (target instanceof HTMLInputElement && target.type === 'radio') {
      if (select) select.value = '';
    }

    this.#syncCustomValue();
    this.#applyState();
  };

  /** @returns {HTMLInputElement[]} */
  get #radios() {
    return /** @type {HTMLInputElement[]} */ ([...this.querySelectorAll('input[type="radio"]')]);
  }

  /** @returns {HTMLSelectElement | null} */
  get #select() {
    return this.querySelector('select');
  }

  /**
   * Mirrors a dropdown choice into the hidden input, and keeps that input
   * disabled whenever it is empty. A disabled field is not submitted, so the
   * named radio stays the only size in the form unless a custom size is picked.
   */
  #syncCustomValue() {
    const field = this.refs.customValue;
    if (!field) return;

    const custom = this.#select?.value ?? '';
    field.value = custom;
    field.disabled = custom === '';
  }

  /** The chosen size, or '' when nothing is selected. @returns {string} */
  get value() {
    return this.#radios.find((radio) => radio.checked)?.value || this.#select?.value || '';
  }

  /**
   * Gates add to cart on a size being chosen.
   *
   * The sticky bar's button is a puppet - it forwards clicks to this same
   * button - so disabling here covers both without touching that component.
   */
  #applyState() {
    if (this.dataset.required !== 'true') return;

    const chosen = this.value !== '';
    const error = this.refs.errorMessage;
    if (error) error.hidden = chosen || !this.dataset.touched;

    for (const button of this.#addToCartButtons()) {
      button.disabled = !chosen;
      button.setAttribute('aria-disabled', String(!chosen));
    }

    if (chosen) this.dataset.touched = 'true';
  }

  /** @returns {HTMLButtonElement[]} */
  #addToCartButtons() {
    const section = this.closest('.shopify-section') ?? document;
    return /** @type {HTMLButtonElement[]} */ ([
      ...section.querySelectorAll('.add-to-cart-button, [id^="BuyButtons-ProductSubmitButton-"]'),
    ]);
  }
}

if (!customElements.get('size-selector')) {
  customElements.define('size-selector', SizeSelectorComponent);
}
